import React, { useState } from 'react';
import { Mail, ChevronUp, ChevronDown, Trash2, Loader2 } from 'lucide-react';
import { WaitlistEntry } from '../types';
import { waitlistApi } from '../services/api';
import WaitlistPositionBadge from './WaitlistPositionBadge';
import WaitlistBadge from './WaitlistBadge';
import { extractApiError } from '../utils/errorUtils'; // Issue #964: Standardized error handling

interface WaitlistEntryRowProps {
  entry: WaitlistEntry & {
    vendor_business_name?: string;
    vendor_email?: string;
  };
  onUpdate?: () => void;
  onRemove?: () => void;
}

/**
 * WaitlistEntryRow - Single row in fair manager's waitlist table
 * Shows vendor info, position, actions to reorder or remove
 */
function WaitlistEntryRow({ entry, onUpdate, onRemove }: WaitlistEntryRowProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleMoveUp = async () => {
    if (entry.position <= 1) return;

    try {
      setError(null);
      setIsUpdating(true);
      await waitlistApi.updatePosition(entry.id, entry.position - 1);
      onUpdate?.();
    } catch (err: unknown) {
      const message = extractApiError(err).message;
      setError(message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleMoveDown = async () => {
    try {
      setError(null);
      setIsUpdating(true);
      await waitlistApi.updatePosition(entry.id, entry.position + 1);
      onUpdate?.();
    } catch (err: unknown) {
      const message = extractApiError(err).message;
      setError(message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemove = async () => {
    try {
      setError(null);
      setIsDeleting(true);
      await waitlistApi.removeFromWaitlist(entry.id, 'Removed by fair manager');
      onRemove?.();
    } catch (err: unknown) {
      const message = extractApiError(err).message;
      setError(message);
      setIsDeleting(false);
      setShowConfirmDelete(false);
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getTimeRemaining = () => {
    if (!entry.offer_expires_at) return null;
    const now = new Date();
    const expiresAt = new Date(entry.offer_expires_at);
    const diff = expiresAt.getTime() - now.getTime();

    if (diff <= 0) return 'Expired';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d`;
    }
    return `${hours}h`;
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors" data-testid="waitlist-entry-item">
      {/* Main Content */}
      <div className="flex items-start gap-4">
        {/* Position Controls */}
        {entry.status === 'waiting' && (
          <div className="flex flex-col gap-1">
            <button
              data-testid="move-up-button"
              onClick={handleMoveUp}
              disabled={isUpdating || entry.position <= 1}
              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Move up"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
            <span className="text-sm font-bold text-gray-700 text-center" data-testid="entry-position">
              #{entry.position}
            </span>
            <button
              data-testid="move-down-button"
              onClick={handleMoveDown}
              disabled={isUpdating}
              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
              title="Move down"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
        )}

        {entry.status !== 'waiting' && (
          <div className="w-12 flex items-center justify-center">
            <span className="text-sm font-bold text-gray-400" data-testid="entry-position">#{entry.position}</span>
          </div>
        )}

        {/* Vendor Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold text-gray-900 truncate" data-testid="vendor-business-name">
              {entry.vendor_business_name || 'Unknown Vendor'}
            </h4>
            {(entry.status === 'waiting' || entry.status === 'notified') ? (
              <span data-testid="entry-position-badge">
                <WaitlistPositionBadge
                  position={entry.position}
                  animated={false}
                  size="sm"
                />
              </span>
            ) : (
              <span data-testid="entry-status-badge">
                <WaitlistBadge status={entry.status} compact />
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-600 mb-2" data-testid="vendor-email">
            <Mail className="w-3 h-3" />
            <span className="truncate">{entry.vendor_email}</span>
          </div>

          {entry.slot_preference && (
            <div className="text-sm text-gray-600 mb-1" data-testid="slot-preference">
              <span className="font-medium">Preference:</span> {entry.slot_preference}
            </div>
          )}

          {entry.notes && (
            <div className="text-sm text-gray-600 bg-gray-50 rounded p-2 border border-gray-200" data-testid="entry-notes">
              <span className="font-medium">Notes:</span> {entry.notes}
            </div>
          )}

          {entry.status === 'notified' && entry.offer_expires_at && (
            <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-teal-50 border border-teal-200 rounded text-xs text-primary-dark" data-testid="offer-expiry">
              <span className="font-medium">Offer expires in:</span>
              <span className="font-bold">{getTimeRemaining()}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-start gap-2">
          {!showConfirmDelete ? (
            <button
              data-testid="remove-entry-button"
              onClick={() => setShowConfirmDelete(true)}
              disabled={isDeleting || entry.status === 'converted'}
              className="p-2 text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              title="Remove from waitlist"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          ) : (
            <div className="flex items-center gap-2" data-testid="remove-confirm-actions">
              <button
                data-testid="waitlist-entry-remove-cancel-button"
                onClick={() => setShowConfirmDelete(false)}
                className="text-xs text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                data-testid="waitlist-entry-remove-confirm-button"
                onClick={handleRemove}
                disabled={isDeleting}
                className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Removing...
                  </>
                ) : (
                  'Confirm'
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Timestamps */}
      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500" data-testid="entry-timestamps">
        <span>Joined: {formatDate(entry.created_at)}</span>
        {entry.notified_at && <span>Notified: {formatDate(entry.notified_at)}</span>}
        {entry.converted_at && <span>Converted: {formatDate(entry.converted_at)}</span>}
        {entry.cancelled_at && <span>Cancelled: {formatDate(entry.cancelled_at)}</span>}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700" data-testid="entry-error-message">
          {error}
        </div>
      )}

      {/* Loading Overlay */}
      {isUpdating && (
        <div className="mt-2 flex items-center justify-center gap-2 text-sm text-gray-600">
          <Loader2 className="w-4 h-4 animate-spin" />
          Updating position...
        </div>
      )}
    </div>
  );
}

export default WaitlistEntryRow;
